/**
 * Catalogue PDF import — a curator attaches a customer's catalogue PDF to a
 * business they claimed; the service renders the pages, finds each product,
 * crops one image per product and produces reviewable product DRAFTS. The
 * curator reviews them page by page and the backend then creates the products.
 *
 * Backed by `/api/v1/catalog-imports` (product service). The wire contract is
 * the json-lib-api package `com.nine.api.catalogimport.model` — every type
 * below mirrors one DTO field for field (dates as ISO strings, UUIDs as
 * strings). Enums are string unions with an `UNKNOWN` member: a value from a
 * newer backend converts to UNKNOWN instead of crashing a screen.
 *
 * Design: NineBackend/product/docs/catalog-import-design.md.
 * UI plan: docs/plans/catalog-import-ui-plan.md.
 *
 * This module has NO imports on purpose: it is plain data + pure predicates, so
 * it can be exercised from a bare node script and imported from anywhere
 * without creating a cycle.
 */

// ── Enums ────────────────────────────────────────────────────────────────────

export type CatalogImportStatus =
  | "AWAITING_UPLOAD"
  | "QUEUED"
  | "PREPARING"
  | "RENDERING"
  | "EXTRACTING"
  | "CROPPING"
  | "VERIFYING"
  | "CONSOLIDATING"
  | "READY_FOR_REVIEW"
  | "COMMITTING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED"
  | "ARCHIVED"
  | "UNKNOWN";

export const CATALOG_IMPORT_STATUSES: readonly CatalogImportStatus[] = [
  "AWAITING_UPLOAD",
  "QUEUED",
  "PREPARING",
  "RENDERING",
  "EXTRACTING",
  "CROPPING",
  "VERIFYING",
  "CONSOLIDATING",
  "READY_FOR_REVIEW",
  "COMMITTING",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
  "ARCHIVED",
  "UNKNOWN",
];

/** The statuses between "file uploaded" and "ready", in pipeline order. */
export const CATALOG_IMPORT_PIPELINE: readonly CatalogImportStatus[] = [
  "AWAITING_UPLOAD",
  "QUEUED",
  "PREPARING",
  "RENDERING",
  "EXTRACTING",
  "CROPPING",
  "VERIFYING",
  "CONSOLIDATING",
  "READY_FOR_REVIEW",
  "COMMITTING",
  "COMPLETED",
];

/** AWAITING_UPLOAD through CONSOLIDATING: the service is working (or waiting to work) on the PDF. */
export function isImportProcessing(status: CatalogImportStatus): boolean {
  switch (status) {
    case "AWAITING_UPLOAD":
    case "QUEUED":
    case "PREPARING":
    case "RENDERING":
    case "EXTRACTING":
    case "CROPPING":
    case "VERIFYING":
    case "CONSOLIDATING":
      return true;
    default:
      return false;
  }
}

/** The import reached an end state and will not change any more. */
export function isImportTerminal(status: CatalogImportStatus): boolean {
  return (
    status === "COMPLETED" ||
    status === "FAILED" ||
    status === "CANCELLED" ||
    status === "ARCHIVED"
  );
}

/** Drafts can be reviewed and edited in this state. */
export function isImportReviewable(status: CatalogImportStatus): boolean {
  return status === "READY_FOR_REVIEW" || status === "COMMITTING";
}

/** The import may still be cancelled (before review starts). */
export function isImportCancellable(status: CatalogImportStatus): boolean {
  return isImportProcessing(status);
}

/**
 * The server is (or soon will be) changing this import without anyone touching
 * it, so a screen showing it should keep refreshing. AWAITING_UPLOAD is left
 * out: it only moves when a browser finishes an upload (or after the 24 h TTL).
 */
export function isImportActive(status: CatalogImportStatus): boolean {
  return (isImportProcessing(status) && status !== "AWAITING_UPLOAD") || status === "COMMITTING";
}

export type CatalogImportStatusFilter =
  | "IN_PROGRESS"
  | "NEEDS_REVIEW"
  | "DONE"
  | "FAILED"
  | "ALL";

/** Chips shown in the Tasks list, in order. */
export const CATALOG_IMPORT_FILTERS: readonly CatalogImportStatusFilter[] = [
  "IN_PROGRESS",
  "NEEDS_REVIEW",
  "DONE",
  "FAILED",
  "ALL",
];

/** Mirrors `CatalogImportStatusFilter.statuses()`; `ALL` selects everything. */
export const CATALOG_IMPORT_FILTER_STATUSES: Record<
  Exclude<CatalogImportStatusFilter, "ALL">,
  readonly CatalogImportStatus[]
> = {
  IN_PROGRESS: [
    "AWAITING_UPLOAD",
    "QUEUED",
    "PREPARING",
    "RENDERING",
    "EXTRACTING",
    "CROPPING",
    "VERIFYING",
    "CONSOLIDATING",
  ],
  NEEDS_REVIEW: ["READY_FOR_REVIEW", "COMMITTING"],
  DONE: ["COMPLETED", "ARCHIVED"],
  FAILED: ["FAILED", "CANCELLED"],
};

export function matchesCatalogImportFilter(
  filter: CatalogImportStatusFilter,
  status: CatalogImportStatus
): boolean {
  return filter === "ALL" || CATALOG_IMPORT_FILTER_STATUSES[filter].includes(status);
}

export type CatalogImportWaitReason = "DAILY_LIMIT_WAIT" | "PROVIDER_BACKOFF" | "UNKNOWN";
export const CATALOG_IMPORT_WAIT_REASONS: readonly CatalogImportWaitReason[] = [
  "DAILY_LIMIT_WAIT",
  "PROVIDER_BACKOFF",
  "UNKNOWN",
];

/** BATCH = overnight (OpenAI Batch API); REALTIME = small PDFs, ready in minutes. */
export type CatalogImportMode = "BATCH" | "REALTIME" | "UNKNOWN";
export const CATALOG_IMPORT_MODES: readonly CatalogImportMode[] = ["BATCH", "REALTIME", "UNKNOWN"];

export type CatalogPageStatus =
  | "PENDING"
  | "RENDERING"
  | "RENDERED"
  | "EXTRACTING"
  | "EXTRACTED"
  | "CROPPED"
  | "FAILED"
  | "SKIPPED"
  | "UNKNOWN";
export const CATALOG_PAGE_STATUSES: readonly CatalogPageStatus[] = [
  "PENDING",
  "RENDERING",
  "RENDERED",
  "EXTRACTING",
  "EXTRACTED",
  "CROPPED",
  "FAILED",
  "SKIPPED",
  "UNKNOWN",
];

export type CatalogPageType =
  | "PRODUCT_GRID"
  | "SINGLE_PRODUCT"
  | "LIFESTYLE"
  | "COVER"
  | "SECTION_DIVIDER"
  | "TEXT_ONLY"
  | "OTHER"
  | "UNKNOWN";
export const CATALOG_PAGE_TYPES: readonly CatalogPageType[] = [
  "PRODUCT_GRID",
  "SINGLE_PRODUCT",
  "LIFESTYLE",
  "COVER",
  "SECTION_DIVIDER",
  "TEXT_ONLY",
  "OTHER",
  "UNKNOWN",
];

export type CatalogDraftStatus =
  | "PROPOSED"
  | "NEEDS_ATTENTION"
  | "APPROVED"
  | "REJECTED"
  | "COMMIT_QUEUED"
  | "COMMITTING"
  | "CREATED"
  | "COMMIT_FAILED"
  | "UNKNOWN";
export const CATALOG_DRAFT_STATUSES: readonly CatalogDraftStatus[] = [
  "PROPOSED",
  "NEEDS_ATTENTION",
  "APPROVED",
  "REJECTED",
  "COMMIT_QUEUED",
  "COMMITTING",
  "CREATED",
  "COMMIT_FAILED",
  "UNKNOWN",
];

/** Fields may be edited in this state (not while queued for creation, never once created). */
export function isDraftEditable(status: CatalogDraftStatus): boolean {
  return (
    status === "PROPOSED" ||
    status === "NEEDS_ATTENTION" ||
    status === "APPROVED" ||
    status === "COMMIT_FAILED"
  );
}

/** The draft became a product; it is read-only from now on. */
export function isDraftFinal(status: CatalogDraftStatus): boolean {
  return status === "CREATED";
}

/** Creation is in flight or queued. */
export function isDraftCommitting(status: CatalogDraftStatus): boolean {
  return status === "COMMIT_QUEUED" || status === "COMMITTING";
}

export type CatalogFlagSeverity = "INFO" | "WARN";

export type CatalogDraftFlag =
  | "MULTI_PRODUCT_PHOTO"
  | "CROP_UNCERTAIN"
  | "CROP_SUSPECT"
  | "LABEL_MISMATCH"
  | "NO_NAME"
  | "LOW_CONFIDENCE"
  | "DUPLICATE_SUSPECT"
  | "DUPLICATE_EXISTING"
  | "SECTION_UNMAPPED"
  | "VARIANTS_FLATTENED"
  | "VARIANT_GROUPED"
  | "LOW_RESOLUTION"
  | "TITLE_GENERATED"
  | "UNKNOWN";

export interface CatalogDraftFlagMeta {
  severity: CatalogFlagSeverity;
  /** Curator-facing wording; identical to `CatalogDraftFlag#getLabel` on the backend. */
  label: string;
}

/**
 * Flag metadata, mirroring the Java enum (severity + label). A WARN flag the
 * curator has not dismissed makes a draft NEEDS_ATTENTION. Flags are hints, not
 * errors: each has a one-tap fix in the review screen.
 */
export const CATALOG_DRAFT_FLAG_META: Record<CatalogDraftFlag, CatalogDraftFlagMeta> = {
  MULTI_PRODUCT_PHOTO: { severity: "WARN", label: "Shares a photo with another product" },
  CROP_UNCERTAIN: { severity: "WARN", label: "Image position was estimated — check the crop" },
  CROP_SUSPECT: { severity: "WARN", label: "Image may be cut off or show more than one item" },
  LABEL_MISMATCH: { severity: "WARN", label: "Name may not match the image" },
  NO_NAME: { severity: "WARN", label: "No name or code found" },
  LOW_CONFIDENCE: { severity: "WARN", label: "The AI was unsure about this product" },
  DUPLICATE_SUSPECT: { severity: "WARN", label: "May be the same product as another draft" },
  DUPLICATE_EXISTING: { severity: "WARN", label: "Looks like a product this business already has" },
  SECTION_UNMAPPED: { severity: "WARN", label: "No store section chosen" },
  VARIANTS_FLATTENED: { severity: "INFO", label: "Variants were split into separate products" },
  VARIANT_GROUPED: { severity: "INFO", label: "Colours or finishes were combined as variants" },
  LOW_RESOLUTION: { severity: "INFO", label: "Small image — fine for now, improve later" },
  TITLE_GENERATED: { severity: "INFO", label: "Title was written from the product code" },
  UNKNOWN: { severity: "INFO", label: "Note from the importer" },
};

export const CATALOG_DRAFT_FLAGS = Object.keys(CATALOG_DRAFT_FLAG_META) as CatalogDraftFlag[];

export const isCatalogFlagWarning = (flag: CatalogDraftFlag): boolean =>
  CATALOG_DRAFT_FLAG_META[flag].severity === "WARN";

export type CatalogMediaRole = "PRIMARY" | "EXTRA" | "UNKNOWN";
export const CATALOG_MEDIA_ROLES: readonly CatalogMediaRole[] = ["PRIMARY", "EXTRA", "UNKNOWN"];

/** How the box of a crop was found; MODEL_SNAPPED and MANUAL are the less certain ones. */
export type CatalogCropSource =
  | "PDF_IMAGE"
  | "EDGE"
  | "MERGED"
  | "MODEL_SNAPPED"
  | "MANUAL"
  | "UNKNOWN";
export const CATALOG_CROP_SOURCES: readonly CatalogCropSource[] = [
  "PDF_IMAGE",
  "EDGE",
  "MERGED",
  "MODEL_SNAPPED",
  "MANUAL",
  "UNKNOWN",
];

/** Request-side only. */
export type CatalogDraftAction = "APPROVE" | "REJECT" | "RESTORE" | "UNAPPROVE";
export type CatalogDraftSelector = "IDS" | "PAGE" | "ALL_UNFLAGGED" | "ALL_PROPOSED";

// ── Shared value types ───────────────────────────────────────────────────────

/**
 * A rectangle on a page in fractions of the page (0..1): x/y are the top-left
 * corner, w/h the size. Valid at any render resolution.
 */
export interface NormalizedBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Smallest side accepted for a manual crop (2% of the page) — INVALID_CROP_BOX otherwise. */
export const NORMALIZED_BOX_MIN_SIDE = 0.02;

/** Same rule as `NormalizedBox#isValid` on the backend. */
export function isValidNormalizedBox(box: NormalizedBox): boolean {
  const finite =
    Number.isFinite(box.x) && Number.isFinite(box.y) && Number.isFinite(box.w) && Number.isFinite(box.h);
  return (
    finite &&
    box.x >= 0 &&
    box.y >= 0 &&
    box.w >= NORMALIZED_BOX_MIN_SIDE &&
    box.h >= NORMALIZED_BOX_MIN_SIDE &&
    box.x + box.w <= 1 + 1e-9 &&
    box.y + box.h <= 1 + 1e-9
  );
}

/** One key/value attribute of a product, e.g. "Thickness" / "15 mm". */
export interface CatalogAttribute {
  key: string;
  value: string;
}

/**
 * A stored failure on an import, page, crop or draft (CatalogStoredErrorDTO).
 * `message` is curator-safe. `code` is a CatalogImportError code ("16255"); show
 * it only inside a "details" disclosure.
 */
export interface CatalogStoredError {
  code: string;
  message: string;
  retryable: boolean;
  at: string | null;
}

export interface CatalogImportProgress {
  pagesRendered: number;
  pagesExtracted: number;
  pagesFailed: number;
  draftsTotal: number;
  /** Drafts that still have a warning the curator has not dismissed. */
  draftsFlagged: number;
  draftsApproved: number;
  draftsRejected: number;
  draftsCreated: number;
  draftsCommitFailed: number;
  stageStartedAt: string | null;
  /** Best-effort; null when unknown. */
  estimatedReadyAt: string | null;
}

/** What the upload screen needs before the curator picks a file (`GET /config`). */
export interface CatalogImportConfig {
  enabled: boolean;
  maxFileBytes: number;
  maxPagesPerImport: number;
  /** At or below this many pages the AI runs in real time; above it overnight. */
  realtimeMaxPages: number;
  /** Pages the caller can still import today. */
  remainingPagesToday: number;
  retentionDays: number;
}

/** A section of the business tree, as offered to the model (SectionSchema). */
export interface CatalogImportSectionHint {
  name: string;
  subsections?: string[];
}

/** Listing context stamped onto every product of an import (same fields the bulk photo flow sends). */
export interface CatalogImportContext {
  latitude?: number;
  longitude?: number;
  /** e.g. "AUTO". */
  contactMode?: string;
  /** e.g. "new_product"; the server falls back to the business's listing type. */
  listingType?: string;
  sellerName?: string;
  sections?: CatalogImportSectionHint[];
}

// ── Responses ────────────────────────────────────────────────────────────────

export interface CatalogSectionMap {
  sectionMapId: string;
  /** The label as printed in the PDF, e.g. "Quartz / Crystal Series". */
  sourceLabel: string;
  sectionId: string | null;
  sectionName: string | null;
  autoMatched: boolean;
  draftCount: number;
}

/** One page in the review rail (also in the import detail). */
export interface CatalogPageSummary {
  pageNo: number;
  status: CatalogPageStatus;
  pageType: CatalogPageType;
  thumbUrl: string | null;
  draftCount: number;
  flaggedCount: number;
  approvedCount: number;
  error: CatalogStoredError | null;
}

/**
 * One import as shown in the Tasks tab and at the top of the review screen.
 * `pages` and `sectionMaps` are filled only by the detail endpoint; null in list rows.
 */
export interface CatalogImport {
  importId: string;
  businessId: string;
  businessName: string | null;
  fileName: string;
  fileSizeBytes: number;
  pageCount: number | null;
  selectedPageCount: number | null;
  pageRanges: string | null;
  instructions: string | null;
  status: CatalogImportStatus;
  waitReason: CatalogImportWaitReason | null;
  mode: CatalogImportMode | null;
  progress: CatalogImportProgress;
  submittedBy: string | null;
  submittedByName: string | null;
  createdAt: string | null;
  startedAt: string | null;
  readyAt: string | null;
  completedAt: string | null;
  /** When the stored pages and images will be removed; the UI warns shortly before. */
  cleanupAt: string | null;
  /** Import-level failure, when status is FAILED. */
  error: CatalogStoredError | null;
  sectionMaps: CatalogSectionMap[] | null;
  pages: CatalogPageSummary[] | null;
}

export interface CatalogHeadings {
  collection: string | null;
  series: string | null;
}

/** A candidate product area found on a page (a dashed suggestion in the crop tool). */
export interface CatalogRegion {
  /** Stable within the page, e.g. "r3". */
  regionId: string;
  box: NormalizedBox;
  source: CatalogCropSource;
  /** Drafts that already use this region. */
  usedByDraftIds: string[];
}

export interface CatalogCropVerification {
  singleProduct: boolean | null;
  complete: boolean | null;
  matchesLabel: boolean | null;
  /** Short curator-safe description of the problem, if any. */
  issue: string | null;
}

export interface CatalogCrop {
  cropId: string;
  pageNo: number;
  box: NormalizedBox;
  source: CatalogCropSource;
  /** Signed, short-lived; refetch the page when it expires. */
  url: string | null;
  widthPx: number;
  heightPx: number;
  verification: CatalogCropVerification | null;
}

/** One image of a draft, in display order. */
export interface CatalogDraftMedia {
  cropId: string;
  role: CatalogMediaRole;
  url: string | null;
}

export interface CatalogVariant {
  label: string;
  code: string | null;
  cropId: string | null;
  url: string | null;
}

/** The existing product a draft looks like. */
export interface CatalogDuplicateOf {
  productId: string;
  title: string | null;
  thumbUrl: string | null;
}

/** One proposed product. */
export interface CatalogDraft {
  draftId: string;
  importId: string;
  status: CatalogDraftStatus;
  /** All flags the importer raised, dismissed ones included (see dismissedFlags). */
  flags: CatalogDraftFlag[];
  dismissedFlags: CatalogDraftFlag[];
  /** 0..1, the AI's own confidence. */
  confidence: number;
  /** Optimistic-lock version; send it back with every edit. */
  version: number;
  pageNos: number[];
  title: string | null;
  description: string | null;
  code: string | null;
  category: string | null;
  attributes: CatalogAttribute[];
  sectionId: string | null;
  /** The series/collection label from the PDF, when known. */
  sectionLabel: string | null;
  media: CatalogDraftMedia[];
  variants: CatalogVariant[];
  /** Drafts sharing one photo carry the same id. */
  multiGroupId: string | null;
  duplicateOf: CatalogDuplicateOf | null;
  /** Set once the product exists. */
  productId: string | null;
  error: CatalogStoredError | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
}

/** Flags that still count: raised and not dismissed. */
export function activeDraftFlags(draft: Pick<CatalogDraft, "flags" | "dismissedFlags">): CatalogDraftFlag[] {
  return draft.flags.filter((flag) => !draft.dismissedFlags.includes(flag));
}

/** One page with everything the review canvas needs. */
export interface CatalogPage {
  pageNo: number;
  status: CatalogPageStatus;
  pageType: CatalogPageType;
  /** The page image, signed and short-lived. */
  imageUrl: string | null;
  thumbUrl: string | null;
  widthPx: number;
  heightPx: number;
  headings: CatalogHeadings | null;
  sharedSpecs: CatalogAttribute[];
  regions: CatalogRegion[];
  crops: CatalogCrop[];
  drafts: CatalogDraft[];
  error: CatalogStoredError | null;
  /** When the signed URLs above stop working. */
  urlsExpireAt: string | null;
}

/** One page of drafts (`GET .../drafts`). */
export interface CatalogDraftPage {
  drafts: CatalogDraft[];
  /** Opaque; pass back as `cursor`. Null on the last page. */
  nextCursor: string | null;
}

export interface CatalogSkippedDraft {
  draftId: string;
  /** Curator-safe reason. */
  reason: string;
}

export interface CatalogBulkActionResult {
  updated: number;
  skipped: CatalogSkippedDraft[];
  /** Refreshed counters so the top bar updates without another call. */
  progress: CatalogImportProgress | null;
}

/** Signed upload target (UploadMediaRs): PUT the bytes to `uploadUrl`. */
export interface CatalogUploadTarget {
  uploadUrl: string;
  path: string;
  publicUrl: string | null;
}

/** Result of creating an import, and of asking for a fresh upload URL. */
export interface CreateCatalogImportResult {
  importJob: CatalogImport;
  upload: CatalogUploadTarget | null;
  uploadExpiresAt: string | null;
}

/**
 * Result of a manual crop. The backend returns the new crop and, when asked,
 * the draft it was attached to / created; either may be null if the response
 * only carried the other.
 */
export interface CatalogCropResult {
  crop: CatalogCrop | null;
  draft: CatalogDraft | null;
}

// ── Requests (wire bodies; optional fields are omitted when unset) ───────────

export interface CreateCatalogImportRq {
  businessId: string;
  /** Original file name, at most 200 characters. */
  fileName: string;
  fileSizeBytes: number;
  /** Lower-case hex SHA-256 of the file. */
  sha256: string;
  /** A hint only — the server counts again. */
  pageCount?: number;
  /** e.g. "1-12,15"; omitted = all pages. */
  pageRanges?: string;
  /** At most 2000 characters; followed for this PDF only. */
  instructions?: string;
  context?: CatalogImportContext;
  /** Import again even if the same PDF was imported for this business before. */
  allowDuplicate?: boolean;
}

export interface CatalogDraftBulkActionRq {
  action: CatalogDraftAction;
  selector: CatalogDraftSelector;
  /** Required when selector is IDS. */
  draftIds?: string[];
  /** Required when selector is PAGE. */
  pageNo?: number;
}

export interface CatalogDraftMediaRef {
  cropId: string;
  role: CatalogMediaRole;
}

export interface CatalogVariantRq {
  /** Required, at most 40 characters. */
  label: string;
  code?: string;
  cropId?: string;
}

/**
 * Every field except `version` is optional: absent = unchanged, an explicit
 * empty list = clear. A stale version is rejected with DRAFT_VERSION_CONFLICT.
 */
export interface UpdateCatalogDraftRq {
  version: number;
  title?: string;
  description?: string;
  code?: string;
  category?: string;
  attributes?: CatalogAttribute[];
  sectionId?: string;
  clearSection?: boolean;
  /** Images in display order (first = primary). */
  media?: CatalogDraftMediaRef[];
  variants?: CatalogVariantRq[];
  /** Flags the curator marked "looks fine". */
  dismissFlags?: CatalogDraftFlag[];
}

export interface MergeCatalogDraftsRq {
  /** At least two; the first becomes the base product. */
  draftIds: string[];
  title?: string;
  /** Variant label per merged draft, keyed by draft id. */
  variantLabels?: Record<string, string>;
}

export interface SplitCatalogDraftRq {
  /** Empty or omitted = all variants. */
  variantLabels?: string[];
}

export interface CreateCatalogCropRq {
  box: NormalizedBox;
  /** Add the new image to this draft... */
  attachToDraftId?: string;
  role?: CatalogMediaRole;
  /** ...or create a new draft from it (exactly one of the two). */
  createDraft?: boolean;
}

export interface RerunCatalogPageRq {
  /** At most 500 characters. */
  instructions: string;
}

export interface CatalogSectionMapRq {
  sectionMapId: string;
  /** Existing business section; null with no newSectionName = unmapped. */
  sectionId?: string | null;
  /** Create this section instead of choosing an existing one. */
  newSectionName?: string;
  /** Parent of the new section; omitted = top level. */
  parentSectionId?: string;
}

export interface UpdateCatalogSectionMapsRq {
  maps: CatalogSectionMapRq[];
}

export interface CommitCatalogImportRq {
  /** Commit only these; omitted or empty = every approved draft. */
  draftIds?: string[];
}

// ── UI-side query / limits ───────────────────────────────────────────────────

/** Rows per list page (also the server default). A shorter page is the last one. */
export const CATALOG_IMPORT_PAGE_SIZE = 20;

/** Longest per-PDF instruction the server accepts. */
export const CATALOG_IMPORT_MAX_INSTRUCTIONS = 2000;

/** Longest re-run instruction the server accepts. */
export const CATALOG_IMPORT_MAX_RERUN_INSTRUCTIONS = 500;

/** Longest file name the server accepts. */
export const CATALOG_IMPORT_MAX_FILE_NAME = 200;

export interface CatalogImportListQuery {
  filter: CatalogImportStatusFilter;
  businessId?: string;
  /** The previous page's last `createdAt`. */
  before?: string;
  /** Refresh without touching loading/error state (polling). */
  silent?: boolean;
}

export interface CatalogDraftListQuery {
  importId: string;
  status?: CatalogDraftStatus;
  flag?: CatalogDraftFlag;
  pageNo?: number;
  cursor?: string;
}

/** The business an import is for. The name is display-only. */
export interface CatalogImportBusinessRef {
  businessId: string;
  businessName: string;
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * What every catalogue import thunk rejects with (`.unwrap()` throws this, and
 * the slice's errorMap keeps `message`). Unlike most thunks it carries more than
 * a string: callers branch on `code` (CATALOG_IMPORT_ERROR in
 * services/helpers/CatalogImportErrors.ts) — e.g. 16212 DUPLICATE_IMPORT with
 * `existingImportId`, 16222 DRAFT_VERSION_CONFLICT, 16215/16216 on a review page.
 */
export interface CatalogImportRejection {
  /** Curator-facing text. */
  message: string;
  /** Backend code ("16212"), "HTTP_503" for gateway noise, or a CLIENT_* code. 0 httpStatus = no response. */
  code: string;
  httpStatus: number;
  /** True when trying the same call again may work. */
  retryable: boolean;
  /** DUPLICATE_IMPORT / IMPORT_ALREADY_RUNNING: the import in the way. */
  existingImportId: string | null;
  details: Record<string, unknown> | null;
}

export const isCatalogImportRejection = (value: unknown): value is CatalogImportRejection =>
  !!value &&
  typeof value === "object" &&
  typeof (value as CatalogImportRejection).message === "string" &&
  typeof (value as CatalogImportRejection).code === "string";
